import { useCallback, useRef } from 'react';

import { useCalloutBanner } from '@/hooks/useCalloutBanner';
import { applyCalloutAction, type CalloutKey } from '@/services/userCallouts';
import { Sentry } from '@/util/sentry';

/**
 * Gates a guided tour on its server-backed callout, so it shows once per
 * reader rather than once per device.
 *
 * Fails closed through `useCalloutBanner`: while the callouts query is loading
 * or errored the tour stays hidden, since replaying a tour someone already
 * finished is worse than missing one.
 *
 * `markShown` records a `shown` transition at most once per mount, so a remount
 * of the surface cannot inflate the count. `markSeen` records the terminal
 * `resolve` and optimistically hides the tour everywhere.
 */
export function useTourCallout({ key, enabled }: { key: CalloutKey; enabled: boolean }) {
  const { isVisible, dismiss } = useCalloutBanner({ key, enabled });

  const hasMarkedShown = useRef(false);

  const markShown = useCallback(() => {
    if (hasMarkedShown.current) {
      return;
    }

    hasMarkedShown.current = true;

    applyCalloutAction(key, 'shown').catch((error: unknown) => {
      Sentry.captureException(error, {
        tags: { component: 'useTourCallout', source: key },
        extra: {
          errorMessage: error instanceof Error ? error.message : undefined,
          callout: key,
        },
      });
    });
  }, [key]);

  return { shouldShow: isVisible, markShown, markSeen: dismiss };
}

export type TourCalloutHandles = ReturnType<typeof useTourCallout>;
